import * as React from 'react';
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { withLocalize, LocalizeContextProps, Translate } from 'react-localize-redux';
import { RouteComponentProps, withRouter, Link, NavLink } from 'react-router-dom';
import { Layout, Row, Col, Icon, Button, Dropdown, Menu, Alert, message } from 'antd';
import bindAllOfThis from 'src/utils/BindThisHelper';
import Routes from 'src/core/Routes';
import { MissionState } from "src/redux/CheckpointReducer";
import PlanetBox from 'src/components/mission/PlanetBox';
import LanguageToggle from 'src/components/common/LanguageToggle';
import DebugUtil from "src/utils/DebugUtil";
import ConfigAction from 'src/redux/ConfigActions';
import { ConfigState } from 'src/redux/ConfigReducer';
import FullscreenToggle from 'src/components/common/FullscreenToggle';
import { ReduxStoreModel } from "src/redux/ReduxModel";
import { UserState } from "src/redux/UserReducer";
import AccountApiController from "src/api/AccountApiController";
import UserAction, { EmulateUserType } from "src/redux/UserActions";
import EmulationDTO from "src/models/account/EmulationDTO";
import { Roles } from "src/constants/Roles";

import './Header.less';

const logo = require('src/assets/branding/logo-small-white.png');
const { Header } = Layout;

type EmulationType = {
    id: number;
    name: string;
    type: EmulateUserType
};

interface GeneralHeaderProps extends LocalizeContextProps, RouteComponentProps<{}> {
    renderPlanetBox: boolean;
    renderAudioToggle: boolean;
    renderFullscreenToggle: boolean;
    renderLanguageToggle: boolean;
    renderEmulationBanner: boolean;
    renderBackToSpaceStation: boolean;

    // From Redux
    User: UserState;
    Config: ConfigState;
    Mission: MissionState;
    toggleAudio: () => void;
    stopEmulation: () => Promise<boolean>;
    emulateUser: (id: number, type: string) => Promise<boolean>;
}

interface GeneralHeaderState {
    emulationInfo: EmulationDTO;
}

class GeneralHeader extends React.Component<GeneralHeaderProps, GeneralHeaderState> {
    constructor(props: GeneralHeaderProps) {
        super(props);
        bindAllOfThis(this, GeneralHeader.prototype);

        this.state = {
            emulationInfo: null
        };
    }

    componentWillMount() {
        this.updateEmulationInfo();
    }

    componentWillReceiveProps(newProps: GeneralHeaderProps) {
        if (newProps.User.isEmulating !== this.props.User.isEmulating) {
            this.updateEmulationInfo();
        }
    }

    private getRoleNameTranslation(roleId: number) {
        let role = Roles.FindById(roleId);

        switch (role) {
            case Roles.DistrictAdmin:
                {
                    return this.props.translate("report.admin.roleFilter.DistrictAdmin");
                }
            case Roles.KuderAdmin:
                {
                    return this.props.translate("report.admin.roleFilter.KuderAdmin");
                }

            case Roles.StateAdmin:
                {
                    return this.props.translate("report.admin.roleFilter.StateAdmin");
                }
            case Roles.TeacherCounselorAdmin:
                {
                    return this.props.translate("report.admin.roleFilter.TeacherCounselorAdmin");
                }
            case Roles.SchoolAdmin:
                {
                    return this.props.translate("report.admin.roleFilter.SchoolAdmin");
                }
            default: {
                break;
            }
        }
        return "";
    }

    private updateEmulationInfo() {
        if (this.props.renderEmulationBanner) {
            AccountApiController.getCurrentEmulationInfo().then(result => {
                this.setState({ emulationInfo: result.data });
            });
        }
    }

    private emulationBannerOnClose() {
        // Stop emulation and redirect to emulate user page
        this.props.stopEmulation();
        this.props.history.push(Routes.ADMIN_EMULATE_USER);
    }

    private handleEmulation(id: number, userType: EmulateUserType) {
        // Fire emulation request, which will take some time, and direct user to emulation loading page
        this.props.emulateUser(id, userType).then(result => {
            if (!result) {
                message.error(this.props.translate("admin.common.messageEmulateError"));
                return;
            }
        });
        this.props.history.push(Routes.ADMIN_EMULATE_USER); // Show them a fancy page while we mess with their emulation info
    }

    private renderEmulationBanner() {
        if (this.props.renderEmulationBanner && this.props.User.isEmulating && this.state.emulationInfo) {
            let currentlyEmulating = this.props.translate("admin.header.currentlyEmulating");
            let { state, district, school } = this.state.emulationInfo;

            // This next part is just messy. There are many ways to do this and I just wanted it to be somewhat readable
            let items: EmulationType[] = [
                state != null ? { ...state, type: "state" } : null,
                district != null ? { ...district, type: "district" } : null,
                school != null ? { ...school, type: "school" } : null
            ];

            // Remove nulls
            items = items.filter(x => x);

            // Pop last item to style it separately
            let lastItem = items.pop();

            // Turn objects into react elements
            let isomers = items.map<React.ReactNode>(t => <a key={t.id} onClick={() => this.handleEmulation(t.id, t.type)}>{t.name}</a>);

            // Add out special last element
            isomers.push(<span key={lastItem.id}>{lastItem.name}</span>);

            // Add additional stop section
            isomers.push(<a key={65} onClick={() => this.emulationBannerOnClose()}><Translate id="admin.header.stopEmulationButton" /></a>);

            // Condense items down into one
            let emulationLine = isomers.reduce((prev, curr) => [prev, " || ", curr]);

            return <Alert
                key="emulationBanner"
                type="info"
                showIcon={false}
                message={<span>{currentlyEmulating} - {emulationLine}</span>}
                closeText={<Translate id="admin.header.stopEmulationButton" />}
                onClose={this.emulationBannerOnClose} />;
        }
    }

    private renderLanguageToggle() {
        if (this.props.renderLanguageToggle) {
            return <LanguageToggle />;
        }
        return null;
    }

    private renderDebugMenuItem() {
        if (DebugUtil.isDebugEnabled()) {
            return <Menu.Item tabIndex={-1}>
                <Link tabIndex={-1} to={Routes.WHO_AM_I}>
                    <Button tabIndex={2} key="whoami-button" type="primary">
                        Who Am I?
                    </Button>
                </Link>
            </Menu.Item>;
        }
        return null;
    }

    private renderAudioMenuItem() {
        if (this.props.renderAudioToggle) {
            return <Menu.Item tabIndex={-1}>
                <Button
                    tabIndex={2}
                    key="audio-toggle"
                    type="primary"
                    onClick={this.props.toggleAudio}>
                    {this.props.Config.audioEnabled
                        ? <Translate id="common.disableAudio" />
                        : <Translate id="common.enableAudio" />}
                </Button>
            </Menu.Item>;
        }
        return null;
    }

    private renderFullscreenMenuItem() {
        if (this.props.renderFullscreenToggle) {
            return <Menu.Item tabIndex={-1}>
                <FullscreenToggle />
            </Menu.Item>;
        }
        return null;
    }

    private renderLoginOutMenuItem() {
        if (this.props.User.state === "finished") {
            return <Menu.Item>
                <Link tabIndex={-1} to={Routes.LOGOUT}>
                    <Button tabIndex={2} key="logout-button" type="primary">
                        <Translate id={"common.logoutButton"} />
                    </Button>
                </Link>
            </Menu.Item>;
        }
        else {
            return <Menu.Item tabIndex={-1}>
                <Link tabIndex={-1} to={Routes.LOGIN}>
                    <Button tabIndex={2} key="login-button" type="primary">
                        <Translate id={"common.logoutButton"} />
                    </Button>
                </Link>
            </Menu.Item>;
        }
    }

    private renderBackToSpaceStationButton() {
        if (this.props.renderBackToSpaceStation && this.props.User.state === "finished" && this.props.User.roleID === Roles.Student.id) {
            return <Link tabIndex={-1} to={Routes.STUDENT_HOME}>
                <Button tabIndex={2} className="menuButtonsToggle" key="back-to-space-station-toggle" type="primary">
                    <Translate id={"common.spaceStation"} />
                </Button>
            </Link>;
        }
    }

    private renderUserType() {
        if (this.props.User.state === "finished" && Roles.Student.id !== this.props.User.roleID) {
            let roleName = this.getRoleNameTranslation(this.props.User.roleID);

            return <label className="header-user-type">{roleName}</label>;
        }
    }

    private renderCenterColumns() {
        const menu = <Menu theme={"dark"} className="menuButtons">
            {this.renderDebugMenuItem()}
            {this.renderAudioMenuItem()}
            {this.renderFullscreenMenuItem()}
            <Menu.Divider />
            {this.renderLoginOutMenuItem()}
        </Menu>;
        let isStudent = this.props.User.state === "finished" && this.props.User.roleID === Roles.Student.id;

        let logoCol = isStudent
            ? <Col key="logo-col" span={4} className="primary-nav">
                <img src={logo} alt="Kuder Galaxy" className="kuder-logo" height="100%" width="100%" tabIndex={-1} />
            </Col>
            : <Col key="logo-col" span={4} className="primary-nav">
                <Link to={Routes.BASE_ROUTE} className="logo" tabIndex={-1}>
                    <img src={logo} alt="Kuder Galaxy" className="kuder-logo" height="100%" width="100%" tabIndex={-1} />
                </Link>
            </Col>;

        if (this.props.renderPlanetBox) {
            return [
                logoCol,
                <Col key="planetBox-col" xs={0} md={10} lg={{ span: 11, push: 1 }}>
                    <PlanetBox />
                </Col>,
                <Col key="dropdown-col" xs={20} md={9} lg={7} className="util-nav">
                    {this.renderLanguageToggle()}
                    <Dropdown overlay={menu} trigger={['click']}>
                        <Button tabIndex={2} className="menuButtonsToggle"><Translate id="common.menu" /> <Icon type="bars" theme="outlined" /></Button>
                    </Dropdown>
                </Col>
            ];
        } else {
            return [
                logoCol,
                <Col key="userType-col" span={8} className="center-nav">
                    {this.renderUserType()}
                </Col>,
                <Col key="dropdown-col" span={10} className="util-nav">
                    {this.renderBackToSpaceStationButton()}
                    {this.renderLanguageToggle()}
                    <Dropdown overlay={menu} trigger={['click']}>
                        <Button tabIndex={2} className="menuButtonsToggle"><Translate id="common.menu" /> <Icon type="bars" theme="outlined" /></Button>
                    </Dropdown>
                </Col>
            ];
        }
    }

    render() {
        let hasEmulationBanner = this.props.renderEmulationBanner && this.props.User.isEmulating;

        if (hasEmulationBanner) {
            return <Header className="header general-header">
                <Row className="header-top-row">
                    {this.renderEmulationBanner()}
                </Row>
                <Row className="header-bottom-row" type="flex" justify="space-between">
                    {this.renderCenterColumns()}
                </Row>
            </Header>;
        }

        return <Header className="header general-header">
            <Row className="header-single-row" type="flex" justify="space-between">
                {this.renderCenterColumns()}
            </Row>
        </Header>;
    }
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return ({
        toggleAudio: () => ConfigAction.ToggleAudio(dispatch),
        stopEmulation: () => UserAction.StopEmulation(dispatch),
        emulateUser: (id: number, type: EmulateUserType) => UserAction.EmulateUserV2(dispatch, id, type),
    });
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User,
        Config: state.Config,
        Mission: state.Mission,
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withLocalize(withRouter(GeneralHeader)));



// WEBPACK FOOTER //
// ./src/layouts/GeneralHeader.tsx